'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Button, Badge, Card, CardContent, Picture, Text, Heading } from '../../components/index.js'

import type { ButtonFunctionType, CopyConfig, CreditsShelfSemanticName, CreditsProductCardData } from './types.js'
import { useCreditsShelfContext } from './context.js'

/**
 * 产品卡片组件 Props
 */
export interface CreditsProductCardProps {
  /** 产品数据 */
  product: CreditsProductCardData
  /** 样式类名 */
  className?: string
  /** 是否展示标签系统 */
  showTags?: boolean
  /** 点击了解更多回调 */
  onLearnMore?: (product: CreditsProductCardData) => void
  /** 点击立即购买回调 */
  onShopNow?: (product: CreditsProductCardData) => void
  /** 点击加入购物车回调 */
  onAddToCart?: (product: CreditsProductCardData) => void
  /** 语义化类名 */
  classNames?: Partial<Record<CreditsShelfSemanticName, string>>
  /** 了解更多按钮文本 */
  secondaryButtonText?: string
  /** 了解更多按钮功能 */
  secondaryButtonFun?: ButtonFunctionType
  /** 立即购买按钮文本 */
  primaryButtonText?: string
  /** 立即购买按钮功能 */
  primaryButtonFun?: ButtonFunctionType
  /** 是否展示原价（删除线价格） */
  showOriginalPrice?: boolean
  /** 文案配置 */
  copy?: CopyConfig
  /** 产品图片点击回调 */
  onProductImageClick?: (product: CreditsProductCardData) => void
}

/**
 * 星星图标组件
 */
const StarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 1L10.163 5.279L15 6.001L11.5 9.409L12.326 14.226L8 11.97L3.674 14.226L4.5 9.409L1 6.001L5.837 5.279L8 1Z"
      fill="currentColor"
    />
  </svg>
)

/**
 * CreditsShelf 产品卡片组件
 */
const CreditsProductCard = React.forwardRef<HTMLDivElement, CreditsProductCardProps>(
  (
    {
      product,
      className,
      showTags: showTagsProp,
      onLearnMore: onLearnMoreProp,
      onShopNow: onShopNowProp,
      onAddToCart: onAddToCartProp,
      classNames: classNamesProp,
      secondaryButtonText: secondaryButtonTextProp,
      secondaryButtonFun: secondaryButtonFunProp,
      primaryButtonText: primaryButtonTextProp,
      primaryButtonFun: primaryButtonFunProp,
      showOriginalPrice: showOriginalPriceProp,
      copy: copyProp,
      onProductImageClick: onProductImageClickProp,
    },
    ref
  ) => {
    // 从 Context 读取配置
    const context = useCreditsShelfContext()

    // Props 优先级高于 Context
    const showTags = showTagsProp ?? context?.showTags ?? true
    const showOriginalPrice = showOriginalPriceProp ?? context?.showOriginalPrice ?? true
    const copy = copyProp ?? context?.copy
    const classNames = classNamesProp ?? context?.classNames
    const secondaryButtonText = secondaryButtonTextProp ?? context?.secondaryButtonText
    const secondaryButtonFun = secondaryButtonFunProp ?? context?.secondaryButtonFun
    const primaryButtonText = primaryButtonTextProp ?? context?.primaryButtonText
    const primaryButtonFun = primaryButtonFunProp ?? context?.primaryButtonFun
    const onLearnMore = onLearnMoreProp ?? context?.onLearnMore
    const onShopNow = onShopNowProp ?? context?.onShopNow
    const onAddToCart = onAddToCartProp ?? context?.onAddToCart
    const onProductImageClick = onProductImageClickProp ?? context?.onProductImageClick

    const [primaryLoading, setPrimaryLoading] = React.useState(false)
    const [secondaryLoading, setSecondaryLoading] = React.useState(false)

    // 根据按钮功能类型调用相应的回调函数
    const handleButtonClick = async (buttonFun?: ButtonFunctionType, buttonType?: 'primary' | 'secondary') => {
      if (!buttonFun) return

      const setLoading = buttonType === 'primary' ? setPrimaryLoading : setSecondaryLoading
      setLoading(true)

      try {
        switch (buttonFun) {
          case 'buyNow':
            await onShopNow?.(product)
            break
          case 'addCart':
            await onAddToCart?.(product)
            break
          case 'learnMore':
            await onLearnMore?.(product)
            break
          default:
            break
        }
      } finally {
        setLoading(false)
      }
    }

    // 生成积分抵现提示文本
    const creditTipText = React.useMemo(() => {
      if (!product.creditSavingAmount) return null
      const template = copy?.creditsSavingLabel ?? 'Save up to {amount} with Credits'
      return template.replace('{amount}', product.creditSavingAmount)
    }, [product.creditSavingAmount, copy?.creditsSavingLabel])

    return (
      <Card
        ref={ref}
        className={cn(
          'bg-container-primary hover:bg-container-secondary-0 box-border flex h-full flex-col overflow-hidden border-none',
          className,
          classNames?.productCard
        )}
      >
        {/* 主内容区域 */}
        <CardContent className="lg-desktop:gap-[10px] lg-desktop:px-6 lg-desktop:py-4 relative flex flex-1 flex-col gap-2 p-4">
          {/* 标签 */}
          <div className="lg-desktop:h-[28px] flex h-[24px] gap-1">
            {showTags &&
              product.tags?.map((tag: any, index) =>
                (tag as any).label ? (
                  <Badge
                    key={index}
                    size="sm"
                    variant={(tag.variant as any) || 'outline'}
                    className={cn(tag.variant === 'promotional' ? 'ml-2' : '')}
                    promotionalType={tag.promotionalType}
                  >
                    {tag.label}
                  </Badge>
                ) : (
                  <React.Fragment key={index}>{tag}</React.Fragment>
                )
              )}
          </div>

          {/* 产品图区域 */}
          <div className="product-image-wrapper lg-desktop:size-[196px] mx-auto size-[138px] cursor-pointer overflow-hidden">
            <a
              onClick={() => onProductImageClick?.(product)}
              {...(!onProductImageClick && {
                href: product?.listingLink,
              })}
              rel="noreferrer"
            >
              <Picture
                source={product.image}
                alt={product.name}
                className="aspect-square size-full object-contain"
                imgClassName="w-full h-full object-contain"
              />
            </a>
          </div>

          {/* 内容区域 */}
          <div className="lg-desktop:gap-6 flex flex-1 grow flex-col justify-between gap-4">
            {/* 产品信息 */}
            <div className="lg-desktop:gap-1 flex flex-col gap-0.5">
              <Heading
                as="h3"
                size={2}
                className={cn('text-info-primary line-clamp-2 min-h-[2.4em]', classNames?.productTitle)}
                html={product.custom_name || product.name}
              />
              {product?.description && (
                <Text
                  size={2}
                  className={cn(
                    'text-info-secondary desktop:line-clamp-2 desktop:min-h-[2.4em] line-clamp-2 min-h-[2.4em]',
                    classNames?.productDescription
                  )}
                  html={product.custom_description || product.description}
                />
              )}
            </div>

            {/* 价格区域 */}
            <div className="lg-desktop:gap-2 flex flex-col gap-1.5">
              {/** 价格标签 - Redeem Credits */}
              {product?.priceLabel && (
                <Text
                  size={4}
                  className={cn(
                    'text-marketing-1 desktop:text-[16px] lg-desktop:text-[18px] text-[14px]',
                    classNames?.productPriceLabel
                  )}
                >
                  {product.priceLabel}
                </Text>
              )}

              {/* 价格 */}
              <div className={cn(classNames?.productPrice)}>
                <div className="flex items-baseline gap-1">
                  {product.availableForSale ? (
                    <>
                      <Heading size={2} className="text-info-primary" as="h6">
                        {product.price}
                      </Heading>
                      {showOriginalPrice && product.originalPrice && (
                        <Heading size={2} className="text-info-tertiary line-through" as="h6">
                          {product.originalPrice}
                        </Heading>
                      )}
                    </>
                  ) : (
                    <Heading size={2} className="text-info-tertiary">
                      {copy?.outOfStockLabel ?? 'Sold Out'}
                    </Heading>
                  )}
                </div>
              </div>

              {/* 按钮区域 */}
              <div className={cn('lg-desktop:gap-3 flex flex-nowrap gap-2', classNames?.buttonGroup)}>
                {secondaryButtonText && (
                  <Button
                    variant="secondary"
                    size="base"
                    className={cn('tablet:w-fit w-full', classNames?.secondaryButton)}
                    onClick={() => handleButtonClick(secondaryButtonFun, 'secondary')}
                    disabled={!product.availableForSale && secondaryButtonFun !== 'learnMore'}
                    loading={secondaryLoading}
                    data-headless-type-name="CreditsShelf#ProductCard"
                    data-headless-title-desc-button={`${product.custom_name || product.name}#${product.description || ''}#${secondaryButtonText}`}
                    data-headless-sku={product.sku}
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {primaryButtonText && (
                  <Button
                    variant="primary"
                    size="base"
                    className={cn('tablet:w-fit w-full', classNames?.primaryButton)}
                    onClick={() => handleButtonClick(primaryButtonFun, 'primary')}
                    disabled={!product.availableForSale && primaryButtonFun !== 'learnMore'}
                    loading={primaryLoading}
                    data-headless-type-name="CreditsShelf#ProductCard"
                    data-headless-title-desc-button={`${product.custom_name || product.name}#${product.description || ''}#${primaryButtonText}`}
                    data-headless-sku={product.sku}
                  >
                    {primaryButtonText}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>

        {/* 底部积分抵现提示条 */}
        <div
          className={cn(
            'bg-container-secondary-0 lg-desktop:px-6 lg-desktop:py-4 flex items-center justify-center gap-1 px-4 py-3',
            classNames?.creditTip
          )}
        >
          <div className="bg-info-primary flex size-6 items-center justify-center rounded-full p-1">
            <span className="text-info-white">
              <StarIcon />
            </span>
          </div>
          <Text size={2} className="text-info-primary">
            {creditTipText}
          </Text>
        </div>
      </Card>
    )
  }
)

CreditsProductCard.displayName = 'CreditsShelf.ProductCard'

export { CreditsProductCard }
export default CreditsProductCard
